import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, ChevronRight, Search, CreditCard } from 'lucide-react';
import { getCreditLineSubscribersList } from '../services/creditLineService';
import { useAuth } from '../auth/AuthContext';
import StatusBadge from '../components/common/StatusBadge';
import Pagination from '../components/common/Pagination';

// ── Helpers ───────────────────────────────────────────────────────────────────

const getCurrentRole = () => {
  const authRole = localStorage.getItem('authRole');
  const roleMapping = {
    wallet_superadmin: 'SuperAdmin',
    wallet_ops: 'Ops',
  };
  return roleMapping[authRole] || null;
};

const CREDIT_LINE_STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'pre_approved', label: 'Pre Approved' },
  { value: 'init', label: 'Init' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'closed', label: 'Closed' },
];

// ── Page ──────────────────────────────────────────────────────────────────────

const CreditLineSubscribersList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const currentRole = getCurrentRole();

  const PAGE_SIZE = 20;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, items: PAGE_SIZE, total: 0, total_pages: 0 });

  const [nameInput, setNameInput] = useState(() => searchParams.get('search') || '');
  const [nameFilter, setNameFilter] = useState(() => searchParams.get('search') || '');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') || '');
  const [page, setPage] = useState(() => Number(searchParams.get('page') || 1));

  const debounceRef = useRef(null);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setNameFilter(nameInput.trim());
      setPage(1);
    }, 700);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [nameInput]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (page > 1) params.set('page', String(page));
    if (nameFilter.trim()) params.set('search', nameFilter.trim());
    if (statusFilter) params.set('status', statusFilter);
    setSearchParams(params, { replace: true });
  }, [page, nameFilter, statusFilter, setSearchParams]);

  const [statusCounts, setStatusCounts] = useState({});
  const [countsLoading, setCountsLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (authLoading || !isAuthenticated || !currentRole) return;
    setLoading(true);
    setError('');
    try {
      const res = await getCreditLineSubscribersList(currentRole, {
        page, items: PAGE_SIZE, search: nameFilter, status: statusFilter,
      });
      if (!res?.metadata?.api_status) throw new Error('API returned an error status');
      const pg = res.msg.pagination || {};
      const totalPages = Math.ceil((pg.total || 0) / (pg.items || PAGE_SIZE));
      setItems(res.msg.items || []);
      setPagination({ ...pg, total_pages: totalPages });
    } catch (err) {
      setError(err.message || 'Failed to load credit line subscribers.');
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [authLoading, isAuthenticated, currentRole, page, nameFilter, statusFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Global per-status counts (across all pages), scoped to the current search
  // term so the tab counts stay consistent with what search would return.
  // The list endpoint only paginates one status at a time, so each tab's count
  // is fetched with items=1 — just to read pagination.total — in parallel.
  const fetchStatusCounts = useCallback(async () => {
    if (authLoading || !isAuthenticated || !currentRole) return;
    setCountsLoading(true);
    try {
      const results = await Promise.all(
        CREDIT_LINE_STATUS_OPTIONS.map((opt) =>
          getCreditLineSubscribersList(currentRole, { page: 1, items: 1, search: nameFilter, status: opt.value })
            .then((res) => [opt.value, res?.msg?.pagination?.total ?? 0])
            .catch(() => [opt.value, null])
        )
      );
      setStatusCounts(Object.fromEntries(results));
    } finally {
      setCountsLoading(false);
    }
  }, [authLoading, isAuthenticated, currentRole, nameFilter]);

  useEffect(() => { fetchStatusCounts(); }, [fetchStatusCounts]);

  if (!currentRole) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">You do not have permission to access credit line subscribers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-4 sm:p-8 flex justify-center">
      <div className="w-full max-w-7xl space-y-6">

        {/* ── Back button + status tabs ── */}
        <div className="flex items-center gap-0 flex-wrap">
          <button
            onClick={() => navigate('/subscriber-wallet')}
            className="px-4 py-2 text-sm text-gray-600 hover:text-white hover:bg-blue-600 rounded-md transition-colors border border-gray-300 hover:border-blue-600 mr-6 shrink-0"
          >
            <ArrowLeft className="w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5" />
            Back to Home
          </button>

          <div className="border-b border-gray-200 w-full overflow-x-auto">
            <nav className="flex gap-0" aria-label="Credit line status tabs">
              {CREDIT_LINE_STATUS_OPTIONS.map((opt) => {
                const selected = statusFilter === opt.value;
                const count = statusCounts[opt.value];
                return (
                  <button
                    key={opt.value}
                    onClick={() => { setStatusFilter(opt.value); setPage(1); }}
                    className={`px-5 py-2.5 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      selected
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    {opt.label}
                    <span className={`ml-1.5 text-xs ${selected ? 'text-blue-500' : 'text-gray-400'}`}>
                      {countsLoading || count == null ? '···' : `(${count})`}
                    </span>
                  </button>
                );
              })}
            </nav>
          </div>
        </div>

        <div>
          <h1 className="text-xl font-bold text-gray-900 leading-tight">Credit Line Subscribers</h1>
          <p className="text-sm text-gray-400">Track credit line accounts and lender status</p>
        </div>

        {error && (
          <div className="px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700 flex items-center justify-between">
            <span>{error}</span>
            <button onClick={fetchData} className="text-xs underline ml-4 hover:text-red-900">Retry</button>
          </div>
        )}

        {/* ── Search bar ── */}
        <div className="sticky top-4 z-20 bg-white rounded-2xl shadow-sm border border-gray-200 p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name, mobile, ref code"
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-50"
              value={nameInput}
              onChange={(e) => setNameInput(e.target.value)}
            />
          </div>
        </div>

        {/* ── Table card ── */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900">Subscribers</h2>
            {!loading && pagination.total > 0 && (
              <span className="text-sm text-gray-500">{pagination.total} subscriber{pagination.total !== 1 ? 's' : ''}</span>
            )}
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Subscriber</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mobile</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ref Code</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Credit Line Status</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Lenders</th>
                  <th className="px-6 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((__, j) => (
                        <td key={j} className="px-6 py-4">
                          <div className="h-4 bg-gray-100 rounded animate-pulse" style={{ width: `${50 + (j * 13) % 40}%` }} />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : items.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-12">
                      <div className="flex flex-col items-center gap-3 text-gray-400">
                        <div className="w-14 h-14 rounded-2xl bg-gray-50 flex items-center justify-center">
                          <CreditCard className="w-6 h-6 text-gray-300" />
                        </div>
                        <p className="text-sm font-medium text-gray-500">No credit line subscribers found</p>
                      </div>
                    </td>
                  </tr>
                ) : (
                  items.map((item) => {
                    const lenders = item.lenders || [];
                    const initial = (item.subscriber_name || '?').charAt(0).toUpperCase();
                    return (
                      <tr
                        key={item.id}
                        className="group hover:bg-blue-50/40 cursor-pointer transition-colors"
                        onClick={() => navigate(`/subscriber-wallet/credit-line-subscribers/${item.id}/detail`)}
                      >
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 text-blue-600 flex items-center justify-center font-semibold text-sm shrink-0">
                              {initial}
                            </div>
                            <span className="text-sm font-medium text-gray-900 truncate">{item.subscriber_name || '—'}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 truncate">
                          {item.mobile || '—'}
                        </td>
                        <td className="px-6 py-4 text-sm font-medium text-gray-900 truncate">
                          {item.ref_code || '—'}
                        </td>
                        <td className="px-6 py-4 text-center">
                          {item.credit_line_account ? (
                            <StatusBadge status={item.credit_line_account.status} label={item.credit_line_account.status_display} />
                          ) : '—'}
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500 text-center">
                          {lenders.length}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-gray-500 transition-colors ml-auto" />
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.total_pages}
            totalItems={pagination.total}
            startIdx={(pagination.page - 1) * pagination.items}
            pageSize={pagination.items}
            onPageChange={setPage}
          />
        </div>
      </div>
    </div>
  );
};

export default CreditLineSubscribersList;
